import { useState, useEffect, useCallback, useMemo } from 'react';
import axios from '../utils/axiosInstance';

/**
 * Hook for metaobject definitions list, sync status, and search filtering.
 */
export default function useMetaobjectDefinitions() {
  const [definitions, setDefinitions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [syncModalOpen, setSyncModalOpen] = useState(false);
  const [lastSynced, setLastSynced] = useState(null);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(timer);
  }, [search]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [defsRes, syncRes] = await Promise.all([
        axios.get(`/api/transly/metaobjects/definitions/`),
        axios.get(`/api/transly/metaobjects/sync/`),
      ]);
      setDefinitions(defsRes.data.definitions || []);
      setLastSynced(syncRes.data.last_completed || null);
    } catch (err) {
      setError(
        err.response?.data?.error || err.message || 'Failed to load definitions'
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const filteredDefinitions = useMemo(() => {
    if (!debouncedSearch) return definitions;
    const term = debouncedSearch.toLowerCase();
    return definitions.filter(
      (d) =>
        (d.type || '').toLowerCase().includes(term) ||
        (d.name || '').toLowerCase().includes(term)
    );
  }, [definitions, debouncedSearch]);

  return {
    definitions,
    filteredDefinitions,
    loading,
    error,
    search,
    setSearch,
    syncModalOpen,
    setSyncModalOpen,
    lastSynced,
    fetchData,
  };
}
